// Instructional-language selector. Sits in the account cluster at the bottom
// of the sidebar (with settings / sign out), because it's a preference, not
// a page. The switch never touches practice items, mocks, or transcripts —
// exam content stays English by design. RTL languages set dir on the body
// so lesson/walkthrough pages render right-to-left when active.
import React, { useEffect, useState } from 'react';
import { Globe, Check } from 'lucide-react';

import { useApp } from '@/lib/store';
import { LANGS, langDir } from '@/lib/lang';
import { cn } from '@/lib/utils';

export default function LangSwitcher({ compact = false, direction = 'down' }) {
  const { lang, setLang } = useApp();
  const [open, setOpen] = useState(false);
  const active = LANGS.find((l) => l.code === lang) || LANGS[0];

  useEffect(() => {
    // Mirror the choice on <html> so lesson/walkthrough content flips to RTL
    // (Arabic + Farsi) without every page needing its own wrapper. Native
    // English exam text inside an rtl page still lays out correctly because
    // the highlighted spans use explicit LTR wrapping in the reader.
    document.documentElement.setAttribute('lang', lang);
    document.documentElement.setAttribute('data-lesson-dir', langDir(lang));
  }, [lang]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!e.target.closest('[data-testid="lang-switcher"]')) setOpen(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [open]);

  return (
    <div className="relative" data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="lang-switcher-button"
        aria-label={`Learning language: ${active.native}`}
        className={cn(
          'flex w-full items-center gap-2 border border-slate-800 bg-slate-900/40 text-left text-slate-300 transition-colors hover:bg-slate-900 hover:text-white',
          compact ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm'
        )}
      >
        <Globe className={compact ? 'h-3.5 w-3.5 shrink-0 text-slate-500' : 'h-4 w-4 shrink-0 text-slate-500'} />
        <span className="min-w-0 flex-1 truncate">
          <span className="mono-stat mr-1.5 !text-[9px] !text-slate-500">lang</span>
          {active.native}
        </span>
      </button>
      {open && (
        <div
          role="listbox"
          data-testid="lang-switcher-menu"
          className={cn(
            'absolute left-0 right-0 z-40 border border-slate-800 bg-slate-950 shadow-2xl',
            direction === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
          )}
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              role="option"
              aria-selected={l.code === lang}
              data-testid={`lang-option-${l.code}`}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                l.code === lang ? 'bg-slate-900 text-white' : 'text-slate-400 hover:bg-slate-900 hover:text-white'
              )}
              dir={l.dir}
            >
              <span className="flex-1 truncate">{l.native}</span>
              {l.code === lang && <Check className="h-3.5 w-3.5 shrink-0 text-indigo-400" />}
              <span className="mono-stat !text-[9px] !text-slate-600" dir="ltr">{l.code}</span>
            </button>
          ))}
          <div className="border-t border-slate-800 px-3 py-2 text-[10px] leading-snug text-slate-500" dir="ltr">
            Lessons + walkthroughs change language. Practice items and audio stay in English (exam content).
          </div>
        </div>
      )}
    </div>
  );
}
